// material-ui
import {
  Grid2 as Grid,
  Paper,
  Stack,
  Typography,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  TextField,
} from '@mui/material'
// import { TextField } from 'components/@extended/TextField'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePlatform } from 'hooks/usePlatform'
import { PlatformCreateOrganizationDTO } from 'types/platform'
import useConfig from 'hooks/useConfig'
import { ThemeMode } from 'types/config'
import { useAuth0 } from '@auth0/auth0-react'
import { analytics } from 'utils/analytics'

// project import

// ================================|| RESET PASSWORD ||================================ //

function SignUp() {
  const { logout, isAuthenticated } = useAuth0()

  const navigate = useNavigate()
  const { createOrganization } = usePlatform('') // Token is currently not requirdd for signup
  const { onChangeMode } = useConfig()
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    verifyPassword: '',
    validation: '',
  })
  const [emailError, setEmailError] = useState('')
  const [fieldErrors, setFieldErrors] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
  })
  const [passwordError, setPasswordError] = useState('')
  const [verifyPasswordError, setVerifyPasswordError] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      logout({
        logoutParams: {
          returnTo: window.location.origin + '/signup',
        },
      })
    }
    onChangeMode(ThemeMode.LIGHT)
    analytics.track('signup_started')
  }, [])

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePersonName = (value: string): boolean => {
    // Allow letters, spaces, hyphens, apostrophes, and periods
    const nameRegex = /^[a-zA-Z\s\-'.]*$/
    return nameRegex.test(value)
  }

  const validateCompanyName = (value: string): boolean => {
    // Allow letters, numbers, spaces, and common company symbols: & . , - _ ( ) +
    const companyRegex = /^[a-zA-Z0-9\s&.,'"\-_()]+$/
    return companyRegex.test(value)
  }

  const validatePassword = (password: string): { isValid: boolean; message: string } => {
    if (password.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters long' }
    }
    if (!/[A-Z]/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one uppercase letter' }
    }
    if (!/[a-z]/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one lowercase letter' }
    }
    if (!/[0-9]/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one number' }
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one special character' }
    }
    return { isValid: true, message: '' }
  }

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setFormData({ ...formData, [field]: value })

    if (field === 'email') {
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address')
      } else {
        setEmailError('')
      }
    } else if (field === 'firstName' || field === 'lastName') {
      if (value && !validatePersonName(value)) {
        setFieldErrors({
          ...fieldErrors,
          [field]: 'Only letters, spaces, hyphens, apostrophes, and periods are allowed',
        })
      } else {
        setFieldErrors({ ...fieldErrors, [field]: '' })
      }
    } else if (field === 'companyName') {
      if (value && !validateCompanyName(value)) {
        setFieldErrors({
          ...fieldErrors,
          companyName: 'Only letters, numbers, and standard company symbols are allowed',
        })
      } else {
        setFieldErrors({ ...fieldErrors, companyName: '' })
      }
    } else if (field === 'password') {
      if (value) {
        const validation = validatePassword(value)
        setPasswordError(validation.message)
      } else {
        setPasswordError('')
      }
      // Also check if verifyPassword matches when password changes
      if (formData.verifyPassword && value !== formData.verifyPassword) {
        setVerifyPasswordError('Passwords do not match')
      } else if (formData.verifyPassword) {
        setVerifyPasswordError('')
      }
    } else if (field === 'verifyPassword') {
      if (value && value !== formData.password) {
        setVerifyPasswordError('Passwords do not match')
      } else {
        setVerifyPasswordError('')
      }
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    // Prevent double submission
    if (isSubmitting) {
      return
    }

    let hasErrors = false
    const newFieldErrors = { companyName: '', firstName: '', lastName: '' }

    if (!validateCompanyName(formData.companyName)) {
      newFieldErrors.companyName = 'Only letters, numbers, and standard company symbols are allowed'
      hasErrors = true
    }

    if (!validatePersonName(formData.firstName)) {
      newFieldErrors.firstName = 'Only letters, spaces, hyphens, apostrophes, and periods are allowed'
      hasErrors = true
    }

    if (!validatePersonName(formData.lastName)) {
      newFieldErrors.lastName = 'Only letters, spaces, hyphens, apostrophes, and periods are allowed'
      hasErrors = true
    }

    if (!validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address')
      hasErrors = true
    }

    const passwordValidation = validatePassword(formData.password)
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.message)
      hasErrors = true
    }

    if (formData.password !== formData.verifyPassword) {
      setVerifyPasswordError('Passwords do not match')
      hasErrors = true
    }

    if (hasErrors) {
      setFieldErrors(newFieldErrors)
      return
    }

    console.log('Form submitted:', formData)

    // Set submitting state to prevent double submission
    setIsSubmitting(true)

    // Create the organization and user account via API call
    const dto: PlatformCreateOrganizationDTO = {
      name: formData.companyName,
      ownerEmail: formData.email,
      ownerFirstName: formData.firstName,
      ownerLastName: formData.lastName,
      password: formData.password,
      ownerPhone: '',
      organizationStreetAddress: '',
      organizationCity: '',
      organizationState: '',
      organizationZipCode: '',
      status: 'pending',
      validation: formData.validation,
    }

    try {
      const response = await createOrganization(dto)

      if (response?.error) {
        console.error('Failed to create organization:', response.error)
        analytics.track('signup_error', {
          companyName: formData.companyName,
          email: formData.email,
          error: response.error,
          error_type: 'failed to create organization',
        })
        setIsSubmitting(false)
        navigate('/signup-error', {
          state: { message: response.message || 'An error occurred during sign up.', code: response.errorCode },
        })
      } else if (!response?.organization?.id || !response?.organization?.stripeCustomerId) {
        console.error('Organization created but missing required data:', response)
        analytics.track('signup_error', {
          companyName: formData.companyName,
          email: formData.email,
          error: 'Missing organization ID or Stripe customer ID',
          error_type: 'incomplete_org_response',
        })
        setIsSubmitting(false)
        navigate('/signup-error', {
          state: {
            message: 'Account was created but setup is incomplete. Please contact support.',
            code: 'incomplete_setup',
          },
        })
      } else {
        console.log('Created organization:', response)
        analytics.track('signup_completed', {
          companyName: formData.companyName,
          email: formData.email,
        })
        navigate('/select-subscription', {
          state: {
            stripeSession: response.stripeSession,
            organizationId: response.organization.id,
            stripeCustomerId: response.organization.stripeCustomerId,
          },
        })
      }
    } catch (error) {
      console.error('Error creating organization:', error)
      analytics.track('signup_error', {
        companyName: formData.companyName,
        email: formData.email,
        error: (error as Error).message,
        error_type: 'exception during create organization',
      })
      setIsSubmitting(false)
      navigate('/signup-error', {
        state: { message: 'An error occurred during sign up.', code: 'unknown' },
      })
    }
  }
  return (
    <Grid
      container
      spacing={3}
      sx={{ minHeight: '100vh', paddingTop: { xs: '2vh', md: '5vh' }, px: { xs: 2, sm: 3, md: 0 } }}
    >
      <Grid size={{ xs: 0, md: 4 }}></Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper
          variant="outlined"
          elevation={0}
          sx={{ padding: 1, borderRadius: 2, mx: 'auto', p: { xs: 2, sm: 3 }, textAlign: 'center' }}
        >
          <Stack spacing={{ xs: 1.5, sm: 2 }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 0.5, sm: 1 } }}>
              <img
                src="/PurcentLogo.svg"
                alt="Purcent Logo"
                style={{ maxWidth: '160px', width: '100%', margin: '0 auto' }}
              />
              <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '1.25rem' } }}>
                Create your company's account
              </Typography>
            </Box>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={{ xs: 1.5, sm: 2 }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.companyName}
                  onChange={handleChange('companyName')}
                  error={!!fieldErrors.companyName}
                  helperText={fieldErrors.companyName}
                  required
                />
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  error={!!fieldErrors.firstName}
                  helperText={fieldErrors.firstName}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  error={!!fieldErrors.lastName}
                  helperText={fieldErrors.lastName}
                  required
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  error={!!emailError}
                  helperText={emailError}
                  required
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange('password')}
                  error={!!passwordError}
                  helperText={passwordError}
                  required
                />
                <TextField
                  fullWidth
                  label="Verify Password"
                  type="password"
                  value={formData.verifyPassword}
                  onChange={handleChange('verifyPassword')}
                  error={!!verifyPasswordError}
                  helperText={verifyPasswordError}
                  required
                />
                <input type="hidden" name="validation" value={formData.validation} />
                <FormControlLabel
                  control={<Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />}
                  label={<Typography variant="body2">I agree to the Terms of Service and Privacy Policy</Typography>}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => (window.location.href = 'https://www.purcent.com')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={
                      isSubmitting ||
                      !termsAccepted ||
                      !formData.companyName ||
                      !formData.firstName ||
                      !formData.lastName ||
                      !formData.email ||
                      !formData.password ||
                      !formData.verifyPassword ||
                      !!fieldErrors.companyName ||
                      !!fieldErrors.firstName ||
                      !!fieldErrors.lastName ||
                      !!emailError ||
                      !!passwordError ||
                      !!verifyPasswordError
                    }
                  >
                    {isSubmitting ? 'Submitting...' : 'Continue'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Grid>
      <Grid size={{ xs: 0, md: 4 }}></Grid>
    </Grid>
  )
}

export default SignUp
